import AppContext from "PFApp/app_context";
import EchartsWrapper from "PFCore/helpers/echarts_wrapper";
import { useSkillsDeltaQuery } from "PFCore/hooks/queries/insights/use_skills_delta_query";
import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIsSkillFilterActive } from "../../../hooks/use_is_skill_filter_active";
import { SelectorOption } from "../../../hooks/use_supply_demand_section";
import BarPreview from "../../previews/bar_preview";
import { skillsDeltaChartConfig } from "./configs/skills_delta_chart_config";
import css from "./skills_delta.module.scss";
import SkillsDeltaSelectors, { SortByItem } from "./skills_delta_selectors";

export const SkillsDelta = (): JSX.Element => {
  const {
    store: {
      pages: {
        insights: { selectedFilters }
      }
    }
  } = useContext(AppContext);
  const { t } = useTranslation(["insights", "translation"]);

  const [sortBy, setSortBy] = useState<SortByItem>("active");
  const [selectedSkill, setSelectedSkill] = useState<SelectorOption[]>([]);
  const [selectedN, setSelectedN] = useState(10);
  const [selectedTime, setSelectedTime] = useState(6);
  const isSkillFilterActive = useIsSkillFilterActive();

  const { data, isLoading, error } = useSkillsDeltaQuery({
    params: {
      ...selectedFilters,
      skills: isSkillFilterActive ? "" : selectedSkill?.[0]?.value || "",
      past_n_months: selectedTime,
      // solution to have the whole data collection for inactive and new skills filtering.
      // Once we accept an array of skills on API, we can remove this
      top_n: 1000 //selectedN
    }
  });

  const [newSkills, inactiveSkills] = useMemo(() => {
    const newSkills = data?.new?.map((skill) => skill.skills) || [];
    const inactiveSkills = data?.inactive?.map((skill) => skill.skills) || [];

    return [newSkills, inactiveSkills];
  }, [data]);

  const config = useMemo(() => {
    const selectedSkills = sortBy === "active" ? newSkills : inactiveSkills;
    return skillsDeltaChartConfig(selectedSkills, data, selectedN, t);
  }, [data, sortBy, selectedN]);

  const [nOptions, timeOptions] = useMemo(() => {
    const numbersArray = Array.from(Array(100).keys());
    const time = Array.from(Array(7).keys());
    const nMapped = numbersArray.map((item) => ({ id: item, displayElement: item, item: item }));
    const timeMapped = time.map((item) => ({ id: item + 1, displayElement: item + 1, item: item + 1 }));
    return [nMapped, timeMapped];
  }, []);

  if (error) {
    return <BarPreview content={t("insights:noData")} />;
  }

  if (!data) {
    return <BarPreview animated content={t("translation:loading")} />;
  }

  return (
    <>
      <div className={css.skillsDeltaSection}>
        <h3>{t("insights:skillsDelta")}</h3>
        <SkillsDeltaSelectors
          graphData={data}
          sortBy={sortBy}
          setSortBy={setSortBy}
          selectedSkill={selectedSkill}
          setSelectedSkill={setSelectedSkill}
          loading={isLoading}
          nOptions={nOptions}
          selectedN={selectedN}
          setSelectedN={setSelectedN}
          timeOptions={timeOptions}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
        />
      </div>
      {isLoading && <BarPreview content={t("translation:loading")} />}
      {newSkills.length > 0 ? (
        <EchartsWrapper key={`${sortBy}+`} options={config} height={500} />
      ) : (
        <BarPreview content={t("insights:noData")} />
      )}
    </>
  );
};
